import { TicketEditorProps } from '@/routes/tickets/TicketEditor.types'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { Suspense, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Await, useLoaderData } from 'react-router-typesafe'
import { ticketEditorRouteLoader, TicketEditorLoaderData } from '@/routes/tickets/TicketEditor.route'
import TicketEditorForm from '@/routes/tickets/TicketEditor.form'
import { Rating, ThinRoundedStar } from '@smastrom/react-rating'
import { StatoTicket } from '@/routes/tickets/TicketEditor.enums'
import { isUserRoleAllowed } from '@/routes/utils'
import DocumentHead from '@/components/document-head/DocumentHead'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createTicketWithApi, updateTicketWithApi } from '@/routes/tickets/ticketWrite'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import type { ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import notify from 'devextreme/ui/notify'
import { fetchTicketEditorData, getTicketDetailQueryKey } from '@/routes/tickets/ticketDetail'

const cloneTicket = <T,>(value: T): T => {
  if (typeof structuredClone === 'function') return structuredClone(value)
  return JSON.parse(JSON.stringify(value)) as T
}

const broadcastTicketUpdated = (ticketRecordId?: number | null) => {
  if (typeof window === 'undefined' || ticketRecordId == null) return

  const channel = new BroadcastChannel(`ticket-${ticketRecordId}`)
  channel.postMessage({ type: 'ticket-updated' })
  channel.close()
}

const TicketEditorLoaded = ({
  creating,
  initialTicketData,
  titlePrefix,
  clients,
  states,
  supportApplications,
  categories,
  platforms,
  problemCategories,
  employees,
  userInfo,
  defaultCRUDAllowedRoles,
}: {
  creating: boolean
  initialTicketData: TicketEditorLoaderData | null
  titlePrefix: string
  clients: any[]
  states: any[]
  supportApplications: any[]
  categories: any[]
  platforms: any[]
  problemCategories: any[]
  employees: any[]
  userInfo: any
  defaultCRUDAllowedRoles: any[]
}) => {
  const { clientId, ticketId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { client } = useQsAdminApiManager()
  const numericTicketId = ticketId ? Number(ticketId) : undefined
  const [ticketDraft, setTicketDraft] = useState<ticket | null>(initialTicketData?.ticket ?? null)
  const [formRevision, setFormRevision] = useState(0)

  const ticketDetailQuery = useQuery({
    queryKey: getTicketDetailQueryKey(numericTicketId),
    enabled: !creating && typeof numericTicketId === 'number' && Number.isFinite(numericTicketId),
    queryFn: () => fetchTicketEditorData(numericTicketId!, { clientId }),
    refetchOnWindowFocus: false,
  })

  const resetDraft = useCallback((nextTicket: ticket | null | undefined) => {
    setTicketDraft(nextTicket ? cloneTicket(nextTicket) : null)
    setFormRevision((currentRevision) => currentRevision + 1)
  }, [])

  useEffect(() => {
    if (creating) {
      resetDraft(initialTicketData?.ticket)
    }
  }, [creating, initialTicketData?.ticket, resetDraft])

  useEffect(() => {
    if (!creating && ticketDetailQuery.data?.ticket) {
      resetDraft(ticketDetailQuery.data.ticket)
    }
  }, [creating, resetDraft, ticketDetailQuery.dataUpdatedAt])

  const refreshTicketData = useCallback(async () => {
    if (creating || !numericTicketId) return
    await ticketDetailQuery.refetch()
  }, [creating, numericTicketId, ticketDetailQuery])

  const saveTicketMutation = useMutation({
    mutationFn: async (currentTicket: ticket) => {
      return creating ? createTicketWithApi(client, currentTicket) : updateTicketWithApi(client, currentTicket)
    },
    onSuccess: async (response, currentTicket) => {
      if (creating) {
        notify('Ticket creato con successo', 'success', 3000)
        navigate(`../${response.data.ticketRecordId}`)
        return
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getTicketDetailQueryKey(currentTicket.id) }),
        queryClient.invalidateQueries({ queryKey: ['tickets', 'active', clientId] }),
      ])
      broadcastTicketUpdated(currentTicket.id)
      notify('Ticket salvato con successo', 'success', 3000)
    },
    onError: (error: any) => {
      notify(
        `Errore durante il salvataggio del ticket: ${error?.response?.data?.error?.exceptionMessage ?? error?.response?.data?.error?.message ?? error?.message ?? 'Errore sconosciuto'}`,
        'error',
        3000,
      )
    },
  })

  const currentTicketData = creating ? initialTicketData : ticketDetailQuery.data
  const currentTicket = ticketDraft
  const pageTitle = creating
    ? `${titlePrefix}Nuovo ticket`
    : currentTicket
      ? `${titlePrefix}${currentTicket.ded_Dis}`
      : `${titlePrefix}Ticket`

  if ((!creating && ticketDetailQuery.isLoading) || !currentTicket) {
    return <LoadingPanel />
  }

  return (
    <>
      <h2 className={clientId ? '' : 'content-block'}>
        {pageTitle}
        {currentTicket.stato?.id === StatoTicket.COMPLETATO && currentTicket.rating > 0 && (
          <Rating
            style={{ width: '5%' }}
            value={currentTicket.rating}
            readOnly={true}
            itemStyles={{
              itemShapes: ThinRoundedStar,
              activeFillColor: '#ffb700',
              inactiveFillColor: '#fbf1a9',
            }}
          />
        )}
      </h2>

      <div
        id="editor-ticket-container"
        className={clientId ? 'dx-card responsive-paddings' : 'content-block dx-card responsive-paddings'}
      >
        <TicketEditorForm
          key={formRevision}
          creating={creating}
          ticket={currentTicket}
          clients={clients}
          states={states}
          applications={supportApplications}
          categories={categories}
          platforms={platforms}
          problemCategories={problemCategories}
          employees={employees}
          problemArguments={currentTicketData?.problemArguments}
          isRoleAllowed={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
          userInfo={userInfo}
          isBusy={saveTicketMutation.isPending}
          onSaveTicket={(nextTicket) => {
            saveTicketMutation.mutate(nextTicket)
          }}
          onResetTicket={() => {
            resetDraft((creating ? initialTicketData : ticketDetailQuery.data)?.ticket)
          }}
          onRefreshTicket={refreshTicketData}
        />
      </div>
    </>
  )
}

export const TicketEditor = (props: TicketEditorProps) => {
  const { creating } = props

  const {
    titlePrefix,
    getTicketData,
    getClients,
    getStates,
    getSupportApplications,
    getCategories,
    getPlatforms,
    getProblemCategories,
    getEmployees,
    getUserInfo,
    defaultCRUDAllowedRoles,
  } = useLoaderData<typeof ticketEditorRouteLoader>()

  return (
    <Suspense fallback={<LoadingPanel />}>
      <Await
        resolve={Promise.all([
          getTicketData,
          getClients,
          getStates,
          getSupportApplications,
          getCategories,
          getPlatforms,
          getProblemCategories,
          getEmployees,
          getUserInfo,
        ])}
      >
        {([
          initialTicketData,
          clients,
          states,
          supportApplications,
          categories,
          platforms,
          problemCategories,
          employees,
          userInfo,
        ]) => (
          <TicketEditorLoaded
            creating={creating}
            initialTicketData={initialTicketData}
            titlePrefix={titlePrefix}
            clients={clients.data.value}
            states={states.data.value}
            supportApplications={supportApplications.data.value}
            categories={categories.data.value}
            platforms={platforms.data.value}
            problemCategories={problemCategories.data.value}
            employees={employees.data.value}
            userInfo={userInfo}
            defaultCRUDAllowedRoles={defaultCRUDAllowedRoles}
          />
        )}
      </Await>
    </Suspense>
  )
}

export default TicketEditor
